/**
 * Kit UI condiviso di EdenFlow.
 *
 * Componenti riutilizzabili che incapsulano i token e gli stili ricorrenti
 * (card, bottoni, badge, empty state, header pagina, modali, spinner/skeleton),
 * così non si rifanno a mano in ogni pagina.
 *
 *   import { Button, Card, Badge, EmptyState, PageHeader, Modal, Spinner, Skeleton } from '../components/ui';
 */
import React from 'react';
import { X } from 'lucide-react';

/* ── Spinner ──────────────────────────────────────────────────────────── */
export function Spinner({ size = 15, color = 'currentColor' }) {
  return (
    <span className="eden-spin" style={{
      display: 'inline-block', width: size, height: size,
      border: `2px solid ${color}`, borderTopColor: 'transparent',
      borderRadius: '50%', flexShrink: 0,
    }} />
  );
}

/* ── Button ───────────────────────────────────────────────────────────── */
const BTN_VARIANTS = {
  primary: { background: 'var(--cyan)', color: 'var(--on-accent)', border: '1px solid var(--cyan)' },
  ghost:   { background: 'var(--surface)', color: 'var(--text-muted)', border: '1px solid var(--border)' },
  danger:  { background: 'var(--red-dim)', color: 'var(--red)', border: '1px solid rgba(244,63,94,0.35)' },
  subtle:  { background: 'transparent', color: 'var(--text-muted)', border: '1px solid transparent' },
};
const BTN_SIZES = {
  sm: { height: 32, padding: '0 11px', fontSize: 12 },
  md: { height: 38, padding: '0 15px', fontSize: 13 },
};

export function Button({
  children, variant = 'ghost', size = 'md', icon: Icon, loading = false,
  disabled = false, fullWidth = false, style = {}, ...rest
}) {
  const v = BTN_VARIANTS[variant] || BTN_VARIANTS.ghost;
  const s = BTN_SIZES[size] || BTN_SIZES.md;
  const off = disabled || loading;
  return (
    <button disabled={off} style={{
      ...v, ...s, ...style,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      borderRadius: 9, fontWeight: 600, cursor: off ? 'default' : 'pointer',
      opacity: off ? 0.55 : 1, width: fullWidth ? '100%' : 'auto',
      touchAction: 'manipulation', transition: 'opacity 0.15s, filter 0.15s', whiteSpace: 'nowrap',
    }}
      onMouseEnter={e => { if (!off) e.currentTarget.style.filter = 'brightness(1.08)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
      {...rest}>
      {loading ? <Spinner size={size === 'sm' ? 12 : 14} /> : (Icon && <Icon size={size === 'sm' ? 13 : 15} />)}
      {children}
    </button>
  );
}

/* ── Avatar ───────────────────────────────────────────────────────────── */
const AVATAR_COLORS = ['#a855f7', '#00d4ff', '#10b981', '#f59e0b', '#f43f5e', '#3b82f6', '#ec4899', '#14b8a6'];
// online: true/false → pallino verde; undefined → nessun indicatore
export function Avatar({ name, src, size = 28, online, style = {} }) {
  const clean = (name || '?').trim();
  const initials = clean.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  const color = AVATAR_COLORS[(clean.charCodeAt(0) || 0) % AVATAR_COLORS.length];
  const dot = Math.max(8, Math.round(size * 0.32));
  return (
    <span style={{ position: 'relative', display: 'inline-flex', flexShrink: 0, ...style }}>
      <span title={name} style={{
        width: size, height: size, minWidth: size, borderRadius: '50%',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
        background: src ? 'var(--surface)' : `${color}22`, border: `1.5px solid ${color}55`,
        color, fontSize: size * 0.4, fontWeight: 700, lineHeight: 1,
      }}>
        {src ? <img src={src} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : initials}
      </span>
      {online !== undefined && (
        <span title={online ? 'Online' : 'Offline'} style={{
          position: 'absolute', bottom: -1, right: -1, width: dot, height: dot, borderRadius: '50%',
          background: online ? 'var(--green)' : 'var(--text-dim)',
          border: '2px solid var(--bg2, #14141c)',
        }} />
      )}
    </span>
  );
}

/* ── Card ─────────────────────────────────────────────────────────────── */
export function Card({ children, padding = 'var(--card-pad, 16px 20px)', style = {}, ...rest }) {
  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
      padding, ...style,
    }} {...rest}>
      {children}
    </div>
  );
}

/* ── Badge ────────────────────────────────────────────────────────────── */
const BADGE_TONES = {
  cyan:   { bg: 'var(--cyan-dim)',   color: 'var(--cyan)' },
  green:  { bg: 'var(--green-dim)',  color: 'var(--green)' },
  yellow: { bg: 'var(--yellow-dim)', color: 'var(--yellow)' },
  red:    { bg: 'var(--red-dim)',    color: 'var(--red)' },
  purple: { bg: 'var(--purple-dim)', color: 'var(--purple)' },
  neutral:{ bg: 'var(--bg3)',        color: 'var(--text-muted)' },
};
export function Badge({ children, tone = 'neutral', icon: Icon, style = {} }) {
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      background: t.bg, color: t.color, fontSize: 11, fontWeight: 600,
      padding: '2px 9px', borderRadius: 999, whiteSpace: 'nowrap', ...style,
    }}>
      {Icon && <Icon size={11} />}{children}
    </span>
  );
}

/* ── EmptyState ───────────────────────────────────────────────────────── */
export function EmptyState({ icon: Icon, title, hint, action, style = {} }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
      padding: '44px 24px', textAlign: 'center', ...style,
    }}>
      {Icon && (
        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={22} color="var(--text-dim)" />
        </div>
      )}
      {title && <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>{title}</div>}
      {hint && <div style={{ fontSize: 12.5, color: 'var(--text-dim)', maxWidth: 360, lineHeight: 1.5 }}>{hint}</div>}
      {action}
    </div>
  );
}

/* ── PageHeader ───────────────────────────────────────────────────────── */
export function PageHeader({ title, subtitle, icon: Icon, actions, style = {} }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 22, ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        {Icon && <Icon size={24} color="var(--cyan)" style={{ flexShrink: 0 }} />}
        <div>
          <h1 style={{ fontSize: 21, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.2 }}>{title}</h1>
          {subtitle && <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 3 }}>{subtitle}</div>}
        </div>
      </div>
      {actions && <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

/* ── Skeleton ─────────────────────────────────────────────────────────── */
export function Skeleton({ width = '100%', height = 14, radius = 6, style = {} }) {
  return (
    <span className="eden-skeleton" style={{
      display: 'block', width, height, borderRadius: radius, ...style,
    }} />
  );
}

/* ── Skeleton compositi (placeholder di caricamento pronti all'uso) ──────
   Usali al posto di "Caricamento…": l'app sembra più veloce e non sfarfalla.
     if (loading) return <ListSkeleton rows={6} />;
*/
export function ListSkeleton({ rows = 5, style = {} }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }} aria-busy="true" aria-label="Caricamento in corso">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '14px 16px',
        }}>
          <Skeleton width={38} height={38} radius={10} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 7 }}>
            <Skeleton width={`${55 + (i * 7) % 30}%`} height={12} />
            <Skeleton width={`${30 + (i * 11) % 25}%`} height={10} />
          </div>
          <Skeleton width={56} height={22} radius={999} style={{ flexShrink: 0 }} />
        </div>
      ))}
    </div>
  );
}

export function CardGridSkeleton({ count = 6, minWidth = 240, height = 120, style = {} }) {
  return (
    <div style={{
      display: 'grid', gap: 14,
      gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`, ...style,
    }} aria-busy="true" aria-label="Caricamento in corso">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Skeleton width="60%" height={13} />
          <Skeleton width="40%" height={11} />
          <Skeleton width="100%" height={height - 70 < 24 ? 24 : height - 70} radius={8} style={{ marginTop: 'auto' }} />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({ rows = 6, cols = 4, style = {} }) {
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden', ...style }} aria-busy="true" aria-label="Caricamento in corso">
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={{
          display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16,
          padding: '14px 16px', borderBottom: r < rows - 1 ? '1px solid var(--border)' : 'none',
          background: r === 0 ? 'var(--surface)' : 'transparent',
        }}>
          {Array.from({ length: cols }).map((_, c) => (
            <Skeleton key={c} width={c === 0 ? '70%' : `${40 + (c * 13) % 40}%`} height={11} />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Modal ────────────────────────────────────────────────────────────── */
export function Modal({ open, onClose, title, children, footer, width = 480 }) {
  const panelRef = React.useRef(null);
  const titleId = React.useId();
  React.useEffect(() => {
    if (!open) return undefined;
    const prevFocus = document.activeElement;
    // focus al pannello all'apertura, chiusura con Escape, focus-trap con Tab
    const panel = panelRef.current;
    const focusFirst = () => {
      const els = panel && panel.querySelectorAll('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])');
      (els && els.length ? els[0] : panel)?.focus();
    };
    focusFirst();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose && onClose(); return; }
      if (e.key === 'Tab' && panel) {
        const els = [...panel.querySelectorAll('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')].filter(el => !el.disabled && el.offsetParent !== null);
        if (!els.length) return;
        const first = els[0], last = els[els.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('keydown', onKey); if (prevFocus && prevFocus.focus) prevFocus.focus(); };
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="eden-modal-wrap" style={{ position: 'fixed', inset: 0, zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={onClose} className="eden-backdrop" style={{ position: 'absolute', inset: 0 }} />
      <div ref={panelRef} className="eden-modal-panel" role="dialog" aria-modal="true" aria-labelledby={title ? titleId : undefined} tabIndex={-1} style={{
        position: 'relative', zIndex: 1, width: `min(${width}px, 100%)`, maxHeight: '90vh', overflowY: 'auto', outline: 'none',
        background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--shadow-lg)',
      }}>
        {title && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 18px', borderBottom: '1px solid var(--border)' }}>
            <span id={titleId} style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
            <button onClick={onClose} aria-label="Chiudi" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-dim)', display: 'flex' }}><X size={18} /></button>
          </div>
        )}
        <div style={{ padding: '18px' }}>{children}</div>
        {footer && <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 18px', borderTop: '1px solid var(--border)' }}>{footer}</div>}
      </div>
    </div>
  );
}
